import { Form, useFetcher, useRouteLoaderData} from "@remix-run/react";
import { Fragment, useEffect, useState } from "react";
import { phTrackClientEvent } from "~/analytics.client";
import { Events } from "~/config";
import {
  loader,
  Plan,
  getCurrencyForCountry,
  getUnlimitedSecondBuyPlan,
} from "~/routes/api.get-products";
import { loader as _appLoader } from "~/routes/_app";
import getSymbolFromCurrency from "currency-symbol-map";

interface GetCreditsModalProps {
  countryCode: string;
  conversationId?: string;
}

export default function GetCreditsModal({
  countryCode,
  conversationId
}: GetCreditsModalProps) {
  // 2) Local state
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const fetcher = useFetcher<typeof loader>();
  const unlimitedFetcher = useFetcher<{ error?: string | null }>();

  const routeData = useRouteLoaderData<typeof _appLoader>("routes/_app");
  const defaultPaymentProvider = routeData?.defaultPaymentProvider as string;
  const unlimitedNormalEligible = Boolean(routeData?.unlimitedNormalEligible);

  // Currency must match what credit packs are priced in. Read from the same
  // fetcher response; fall back to the prop while it loads.
  const userCurrency = fetcher.data?.currency ?? getCurrencyForCountry(countryCode || "US");
  const unlimitedPlan = getUnlimitedSecondBuyPlan(userCurrency)
    ?? getUnlimitedSecondBuyPlan("USD");
  const unlimitedAmount = unlimitedPlan ? unlimitedPlan.amount / 100 : 0;
  const unlimitedSymbol = unlimitedPlan
    ? (getSymbolFromCurrency(unlimitedPlan.currency.toUpperCase())
        || unlimitedPlan.currency.toUpperCase())
    : "$";

  // Eligibility (account flag on, exactly 1 prior successful purchase, no
  // active window, lifetime-once not yet consumed) is resolved server-side
  // in the _app loader. We just check the price exists for the currency.
  const showUnlimitedCard = unlimitedNormalEligible && Boolean(unlimitedPlan);

  const purchaseUnlimited = () => {
    if (!unlimitedPlan) return;
    const fd = new FormData();
    fd.set("planId", unlimitedPlan.id);
    fd.set("currency", unlimitedPlan.currency);
    fd.set("provider", defaultPaymentProvider);
    fd.set("redirectTo", typeof window !== "undefined" ? window.location.pathname : "");
    if (conversationId) fd.set("conversationId", conversationId);
    unlimitedFetcher.submit(fd, {
      method: "post",
      action: "/process-payment",
    });
  };

  // 3) Analytics + fetch plans
  useEffect(() => {
    phTrackClientEvent(Events.PURCHASE_CREDITS_POPUP, {});
    fetcher.submit(
      {
        provider: defaultPaymentProvider
      },
      { method: "GET", action: "/api/get-products" }
    );
  }, []);

  // 4) Store fetched plans once data arrives
  useEffect(() => {
    if (fetcher.data) {
      setPlans(fetcher.data.plans);
      //setProvider(fetcher.data.provider);
    }
  }, [fetcher.data]);

  // 5) Set default selection based on plan configuration
  useEffect(() => {
    if (plans.length) {
      // Find the plan marked as default selection
      const defaultPlan = plans.find((p) => p.isDefaultSelection);
      if (defaultPlan) {
        setSelectedPlanId(defaultPlan.id);
      } else {
        // Fallback to first plan if no default is specified
        setSelectedPlanId(plans[0].id);
      }
    }
  }, [plans]);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);


  // Show skeleton rows if data not loaded
  const isLoading = !plans.length && fetcher.state !== "idle";

  return (
    <div className="!border-0 !outline-0 !ring-0" style={{ border: 'none !important', outline: 'none !important' }}>
      <div className="bg-componentBackground border-2 border-border dark:border-red-500 dark:bg-gradient-to-br dark:from-red-950 dark:via-black dark:to-red-900 rounded-3xl p-5 max-w-4xl w-[90vw] md:w-full shadow-[0_0_50px_rgba(255,0,0,0.5),inset_0_0_30px_rgba(255,0,0,0.1),0_20px_60px_rgba(0,0,0,0.8)] z-15">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-black text-primary dark:text-yellow-400 uppercase tracking-wider animate-pulse">
            Get Credits!
          </h1>
        </div>

        <Form method="post" action="/process-payment">
          {/* Mobile Layout - Vertical Stack */}
          <div className="flex md:hidden flex-col gap-2 mb-4">
            {isLoading ? (
              // Show skeleton cards for mobile
              Array.from({ length: 5 }).map((_, i) => <MobilePlanSkeletonCard key={i} />)
            ) : plans.length ? (
              plans.map((product, index) => {
                // Define images for each package in order
                const packageImages = [
                  '/stackofcoins_transparent.webp',    // Package 1
                  '/pileofcoins_transparent.webp',     // Package 2
                  '/purseofcoins_transparent.webp',    // Package 3
                  '/moneybagofcoins_transparent.webp', // Package 4
                  '/boxfullofcoins_transparent.webp'   // Package 5
                ];
                const currentImage = packageImages[index] || packageImages[0];

                // Determine if this package is popular/special
                //const isPopular = product.label && (product.label.toLowerCase().includes('most purchased') || product.label.toLowerCase().includes('popular'));
                const isBestValue = product.label && product.label.toLowerCase().includes('best');

                return (
                  <Fragment key={product.id}>
                    <button
                      type="submit"
                      name="_action"
                      value="purchasePlan"
                      onClick={() => setSelectedPlanId(product.id)}
                      className="relative bg-white border-2 border-border dark:border-red-500 dark:bg-gradient-to-r dark:from-gray-800 dark:to-black rounded-xl p-3 flex items-center justify-between cursor-pointer transition-all duration-300 active:scale-95 min-h-[70px] active:border-primary dark:active:border-yellow-400 w-full text-left"
                    >
                      {/* Badge */}
                      {product.label && (
                        <div className={`absolute -top-1 right-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold shadow-lg z-20 ${isBestValue
                            ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                            : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black'
                          }`}>
                          {product.label.toLowerCase().includes('most purchased') ? 'POPULAR!' :
                            product.label.toLowerCase().includes('best') ? 'BEST VALUE!' : product.label.toUpperCase()}
                        </div>
                      )}

                      {/* Left Side - Icon + Info */}
                      <div className="flex items-center gap-3 flex-1">
                        {/* Coin Icon */}
                        <div className="w-12 h-12 flex items-center justify-center">
                          <img
                            src={currentImage}
                            alt="Coins"
                            className="w-full h-full object-contain drop-shadow-lg"
                          />
                        </div>

                        {/* Package Info */}
                        <div>
                          <div className="text-lg font-bold text-primary dark:text-yellow-400 drop-shadow-lg">
                            {product.credits}
                          </div>
                          <div className="text-text1 dark:text-white text-xs uppercase tracking-wider opacity-90">
                            Credits
                          </div>
                          {product.base_amount > product.amount && (
                            <div className="mt-0.5 flex items-center gap-1.5 whitespace-nowrap">
                              <span className="text-[10px] text-text1 line-through">
                                {product.symbol}{product.base_amount.toFixed(2)}
                              </span>
                              <span className="text-[10px] font-bold text-yellow-400">
                                {Math.round((1 - product.amount / product.base_amount) * 100)}% OFF
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Side - Price */}
                      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-2 px-4 rounded-full font-bold text-sm shadow-lg whitespace-nowrap shrink-0">
                        {product.symbol}{product.amount.toFixed(2)}
                      </div>
                    </button>

                    {/* Sit the unlimited card right after the 175-pack so the
                        two "special offers" for second-time buyers cluster. */}
                    {product.credits === 175 && showUnlimitedCard && unlimitedPlan && (
                      <button
                        type="button"
                        disabled={unlimitedFetcher.state !== "idle"}
                        onClick={purchaseUnlimited}
                        className="relative bg-white border-2 border-emerald-400 dark:bg-gradient-to-r dark:from-emerald-950 dark:via-black dark:to-emerald-900 rounded-xl p-4 flex items-center justify-between cursor-pointer transition-all duration-300 active:scale-95 min-h-[88px] active:border-emerald-300 w-full text-left shadow-[0_0_18px_rgba(16,185,129,0.45)] disabled:opacity-60"
                      >
                        <span
                          className="absolute left-1/2 -translate-x-1/2 -top-3 z-20 px-2.5 py-1 rounded-full text-[10px] font-black shadow-lg bg-gradient-to-r from-yellow-400 via-orange-400 to-rose-500 text-black whitespace-nowrap rotate-6 transition-none"
                        >
                          🎁 BONUS UNLOCKED
                        </span>
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-14 h-14 flex items-center justify-center text-4xl drop-shadow-[0_0_10px_rgba(249,115,22,0.7)]">
                            🔥
                          </div>
                          <div>
                            <div className="text-xl font-bold text-emerald-300 drop-shadow-lg whitespace-nowrap">
                              {unlimitedPlan.durationMinutes} min
                            </div>
                            <div className="text-text1 dark:text-white text-xs uppercase tracking-wider opacity-95 font-semibold">
                              Unlimited Messages
                            </div>
                            <div className="text-emerald-300 text-[10px] mt-0.5 font-semibold">
                              ✦ Profile Boost included
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-2 px-4 rounded-full font-bold text-sm shadow-lg whitespace-nowrap">
                          {unlimitedSymbol}
                          {unlimitedAmount.toFixed(2)}
                        </div>
                      </button>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <div className="text-center text-text1 dark:text-white mt-4">
                No plans available
              </div>
            )}

          </div>

          {/* Desktop Layout - Grid */}
          {/* Grid grows to 6 columns only when the unlimited card is in the
              mix — otherwise it'd leave a phantom empty column on lg. */}
          <div className={`hidden md:grid grid-cols-2 md:grid-cols-3 ${showUnlimitedCard ? "lg:grid-cols-6" : "lg:grid-cols-5"} gap-3 mb-6`}>
            {isLoading ? (
              // Show skeleton cards for desktop
              Array.from({ length: 5 }).map((_, i) => <PlanSkeletonCard key={i} />)
            ) : plans.length ? (
              plans.map((product, index) => {
                // Define images for each package in order
                const packageImages = [
                  '/stackofcoins_transparent.webp',    // Package 1
                  '/pileofcoins_transparent.webp',     // Package 2
                  '/purseofcoins_transparent.webp',    // Package 3
                  '/moneybagofcoins_transparent.webp', // Package 4
                  '/boxfullofcoins_transparent.webp'   // Package 5
                ];
                const currentImage = packageImages[index] || packageImages[0];

                // Determine if this package is popular/special
                //const isPopular = product.label && (product.label.toLowerCase().includes('most purchased') || product.label.toLowerCase().includes('popular'));
                const isBestValue = product.label && product.label.toLowerCase().includes('best');

                return (
                  <Fragment key={product.id}>
                    <button
                      type="submit"
                      name="_action"
                      value="purchasePlan"
                      onClick={() => setSelectedPlanId(product.id)}
                      className="relative bg-white border-2 border-border dark:border-red-500 dark:bg-gradient-to-br dark:from-gray-800 dark:to-black rounded-2xl p-4 text-center cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:scale-105 group hover:border-primary dark:hover:border-yellow-400 hover:shadow-[0_10px_30px_rgba(255,215,0,0.3),0_0_20px_rgba(255,0,0,0.5)]"
                    >
                      {/* Badge */}
                      {product.label && (
                        <div className={`absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs font-bold transform rotate-12 shadow-lg z-20 ${isBestValue
                            ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                            : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black'
                          }`}>
                          {product.label.toLowerCase().includes('most purchased') ? 'POPULAR!' :
                            product.label.toLowerCase().includes('best') ? 'BEST VALUE!' : product.label.toUpperCase()}
                        </div>
                      )}

                      {/* Coin Icon */}
                      <div className="w-20 h-20 mx-auto mb-3 flex items-center justify-center">
                        <img
                          src={currentImage}
                          alt="Coins"
                          className="w-full h-full object-contain drop-shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12"
                        />
                      </div>

                      {/* Credit Amount */}
                      <div className="text-2xl font-bold text-primary dark:text-yellow-400 mb-1 drop-shadow-lg">
                        {product.credits}
                      </div>

                      {/* Credits Label */}
                      <div className="text-text1 dark:text-white text-sm mb-3 uppercase tracking-wider">
                        Credits
                      </div>

                      {/* Discount line: struck original price + % off when discounted */}
                      {product.base_amount > product.amount && (
                        <div className="flex items-center justify-center gap-1.5 mb-1 whitespace-nowrap">
                          <span className="text-xs text-text1 line-through">
                            {product.symbol}{product.base_amount.toFixed(2)}
                          </span>
                          <span className="text-xs font-bold text-yellow-400">
                            {Math.round((1 - product.amount / product.base_amount) * 100)}% OFF
                          </span>
                        </div>
                      )}
                      {/* Price Pill */}
                      <div className="w-full bg-gradient-to-r from-red-600 to-red-800 text-white py-2 px-3 rounded-full font-bold text-sm shadow-lg group-hover:from-yellow-400 group-hover:to-yellow-500 group-hover:text-black transition-all duration-300">
                        {product.symbol}{product.amount.toFixed(2)}
                      </div>
                    </button>

                    {/* Unlimited card sits next to the 175-pack so the two
                        "special offers" for second-time buyers cluster. */}
                    {product.credits === 175 && showUnlimitedCard && unlimitedPlan && (
                      <button
                        type="button"
                        disabled={unlimitedFetcher.state !== "idle"}
                        onClick={purchaseUnlimited}
                        className="relative bg-white border-2 border-emerald-400 dark:bg-gradient-to-br dark:from-emerald-900 dark:via-black dark:to-emerald-950 rounded-2xl p-4 text-center cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:scale-105 group hover:border-emerald-300 hover:shadow-[0_10px_40px_rgba(16,185,129,0.7),0_0_30px_rgba(20,184,166,0.7)] shadow-[0_0_30px_rgba(16,185,129,0.6)] animate-pulsesubtle disabled:opacity-60"
                        style={{ animationDuration: "4s" }}
                      >
                        <div
                          className="absolute left-1/2 -translate-x-1/2 -top-3 z-20 px-2.5 py-1 rounded-full text-[10px] font-black shadow-lg bg-gradient-to-r from-yellow-400 via-orange-400 to-rose-500 text-black whitespace-nowrap rotate-6 transition-none"
                        >
                          🎁 BONUS UNLOCKED
                        </div>
                        <div className="w-20 h-20 mx-auto mb-3 flex items-center justify-center text-5xl drop-shadow-[0_0_15px_rgba(249,115,22,0.7)] transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
                          🔥
                        </div>
                        <div className="text-2xl font-bold text-emerald-300 mb-1 drop-shadow-lg whitespace-nowrap">
                          {unlimitedPlan.durationMinutes} min
                        </div>
                        <div className="text-text1 dark:text-white text-xs uppercase tracking-wider font-semibold leading-tight">
                          Unlimited<br />Messages
                        </div>
                        <div className="text-emerald-300 text-[10px] mt-1 mb-2 font-semibold whitespace-nowrap">
                          ✦ Profile Boost
                        </div>
                        <div className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-2 px-3 rounded-full font-bold text-sm shadow-lg group-hover:from-emerald-400 group-hover:to-teal-400 transition-all duration-300 whitespace-nowrap">
                          {unlimitedSymbol}
                          {unlimitedAmount.toFixed(2)}
                        </div>
                      </button>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <div className="col-span-full text-center text-text1 dark:text-white mt-4">
                No plans available
              </div>
            )}
          </div>

          {/* If a plan is selected, pass hidden inputs */}
          {selectedPlan && (
            <>
              <input type="hidden" name="selectedPlan" value={selectedPlan.id} />
              <input type="hidden" name="planId" value={selectedPlan.id} />
              <input type="hidden" name="currency" value={selectedPlan.currency || 'USD'} />
              <input type="hidden" name="provider" value={defaultPaymentProvider} />
              <input type="hidden" name="enableUpsell" value="true" />
              <input type="hidden" name="redirectTo" value={typeof window !== 'undefined' ? window.location.pathname : ''} />
              {conversationId && (
                <input type="hidden" name="conversationId" value={conversationId} />
              )}
            </>
          )}

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-border dark:border-red-500/30">
            <div className="flex items-center gap-2 text-sm text-text2 dark:text-gray-300">
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-text2 dark:text-gray-300">
              <svg className="w-5 h-5 text-primary dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
              <span>Instant Delivery</span>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}

/** Mobile skeleton card for loading state */
function MobilePlanSkeletonCard() {
  return (
    <div className="relative bg-white border-2 border-border dark:border-red-500 dark:bg-gradient-to-r dark:from-gray-800 dark:to-black rounded-xl p-3 flex items-center justify-between animate-pulse min-h-[70px]">
      {/* Left side - Icon + Info */}
      <div className="flex items-center gap-3 flex-1">
        {/* Fake coin icon */}
        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full" />

        {/* Fake info */}
        <div>
          <div className="h-4 w-10 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
          <div className="h-3 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>

      {/* Right side - Price */}
      <div className="h-6 w-14 bg-gray-200 dark:bg-gray-700 rounded-full" />
    </div>
  );
}

/** Desktop skeleton card for loading state */
function PlanSkeletonCard() {
  return (
    <div className="relative bg-white border-2 border-border dark:border-red-500 dark:bg-gradient-to-br dark:from-gray-800 dark:to-black rounded-2xl p-4 text-center animate-pulse">
      {/* Fake coin icon */}
      <div className="w-20 h-20 mx-auto mb-3 bg-gray-200 dark:bg-gray-700 rounded-full" />

      {/* Fake credits amount */}
      <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded mb-1 mx-auto" />

      {/* Fake credits label */}
      <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-3 mx-auto" />

      {/* Fake price button */}
      <div className="h-8 w-full bg-gray-200 dark:bg-gray-700 rounded-full" />
    </div>
  );
}
